import type {
  LayoutSettingFieldView,
  LayoutSettingSectionView,
  LayoutSettingValue,
} from "~/v3-poc/features/workspace/shared/layoutSettings/layoutSettings.types";
import {
  REACT_GRID_LAYOUT_SCHEMA,
  RGL_SETTING_IDS,
  type RglSettingId,
} from "./reactGridLayoutSettings.schema";
import type { ReactGridLayoutSettings } from "./reactGridLayoutSettings.types";
import { disabledForRglSetting } from "./rglSettings.invariants";
import { readRglNumeric } from "./rglSettings.writers";

export interface RglSettingsSectionSpec {
  id: string;
  label: string;
  description: string;
  fieldIds: ReadonlyArray<RglSettingId>;
}

export const RGL_SETTINGS_SECTIONS: ReadonlyArray<RglSettingsSectionSpec> = [
  {
    id: "grid",
    label: "Grid",
    description:
      "Core grid geometry: column count, row height, margins, padding, row limits, auto-sizing, and compaction direction.",
    fieldIds: [
      "cols",
      "rowHeight",
      "marginX",
      "marginY",
      "containerPaddingMode",
      "containerPaddingX",
      "containerPaddingY",
      "maxRowsLimited",
      "maxRows",
      "autoSize",
      "compactor",
    ],
  },
  {
    id: "drag",
    label: "Drag",
    description:
      "Controls whether widgets are confined to the grid during drag and the pixel threshold before a drag begins.",
    fieldIds: ["bounded", "dragThreshold"],
  },
  {
    id: "resize",
    label: "Resize",
    description:
      "Selects which resize handle directions are active on each widget.",
    fieldIds: ["resizeHandles"],
  },
  {
    id: "position",
    label: "Positioning",
    description:
      "Determines how widget positions are calculated: absolute, CSS transform, or scaled. The scale factor applies only in scaled mode.",
    fieldIds: ["positionStrategy", "positionScale"],
  },
  {
    id: "constraints",
    label: "Constraints",
    description:
      "Bounding, per-axis locking, global min/max widget size, snap-to-grid, and aspect-ratio lock applied to all widgets.",
    fieldIds: [
      "gridBounds",
      "containerBounds",
      "boundedX",
      "boundedY",
      "minMaxSize",
      "globalMinEnabled",
      "globalMinW",
      "globalMinH",
      "globalMaxEnabled",
      "globalMaxW",
      "globalMaxH",
      "snapEnabled",
      "snapX",
      "snapY",
      "aspectEnabled",
      "aspectRatio",
    ],
  },
  {
    id: "externalDrop",
    label: "External drop",
    description:
      "Enables dropping items from outside the grid and sets the placeholder widget dimensions used during an external drop.",
    fieldIds: ["externalDropEnabled", "externalDropW", "externalDropH"],
  },
];

{
  const seen = new Set<RglSettingId>();
  for (const section of RGL_SETTINGS_SECTIONS) {
    for (const id of section.fieldIds) {
      if (seen.has(id)) {
        throw new Error(`Duplicate RGL setting id in sections: ${id}`);
      }
      seen.add(id);
    }
  }
  for (const id of RGL_SETTING_IDS) {
    if (!seen.has(id)) {
      throw new Error(`RGL setting id missing from sections: ${id}`);
    }
  }
}

const readBoolean = (c: ReactGridLayoutSettings, id: RglSettingId): boolean => {
  switch (id) {
    case "maxRowsLimited":
      return c.maxRows.limited;
    case "autoSize":
      return c.autoSize;
    case "bounded":
      return c.bounded;
    case "gridBounds":
      return c.gridBounds;
    case "containerBounds":
      return c.containerBounds;
    case "boundedX":
      return c.boundedX;
    case "boundedY":
      return c.boundedY;
    case "minMaxSize":
      return c.minMaxSize;
    case "globalMinEnabled":
      return c.globalMin.enabled;
    case "globalMaxEnabled":
      return c.globalMax.enabled;
    case "snapEnabled":
      return c.snap.enabled;
    case "aspectEnabled":
      return c.aspect.enabled;
    case "externalDropEnabled":
      return c.externalDrop.enabled;
    default:
      return false;
  }
};

const readSelect = (c: ReactGridLayoutSettings, id: RglSettingId): string => {
  switch (id) {
    case "containerPaddingMode":
      return c.containerPaddingMode;
    case "compactor":
      return c.compactor;
    case "positionStrategy":
      return c.positionStrategy;
    default:
      return "";
  }
};

const fieldValue = (
  c: ReactGridLayoutSettings,
  id: RglSettingId,
  drafts: Partial<Record<RglSettingId, string>>,
): LayoutSettingValue => {
  const control = REACT_GRID_LAYOUT_SCHEMA[id].control;
  switch (control.kind) {
    case "boolean":
      return readBoolean(c, id);
    case "select":
      return readSelect(c, id);
    case "number":
      return drafts[id] ?? String(readRglNumeric(c, id));
    case "handles":
      return c.resizeHandles as ReadonlyArray<string>;
    case "text":
    case "breakpoints":
      return "";
  }
};

export const buildRglSettingsSections = (
  config: ReactGridLayoutSettings,
  drafts: Partial<Record<RglSettingId, string>>,
  errors: Partial<Record<RglSettingId, string>>,
): ReadonlyArray<LayoutSettingSectionView> => {
  return RGL_SETTINGS_SECTIONS.map((section) => ({
    id: section.id,
    label: section.label,
    description: section.description,
    fields: section.fieldIds.map((id): LayoutSettingFieldView => {
      const descriptor = REACT_GRID_LAYOUT_SCHEMA[id];
      const { disabled, reason } = disabledForRglSetting(config, id);
      return {
        descriptor,
        value: fieldValue(config, id, drafts),
        error: errors[id],
        disabled,
        disabledReason: reason,
      };
    }),
  }));
};
